import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Copy, Monitor, Smartphone } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput, TextArea, EmptyState } from "@/components/admin/ui";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const slugify = (s) => s.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
const pageUrl = (slug) => `${window.location.origin}/p/${slug}`;

export default function PagesManager() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "pages");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({});
  const [view, setView] = useState("edit");
  const [device, setDevice] = useState("desktop");

  const load = () => api.get("/pages").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ title: "", slug: "", subtitle: "", image: "", body: "", published: true, seo_title: "", seo_description: "", og_image: "" }); setView("edit"); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setView("edit"); setEditing(p.id); };

  const copyLink = async (slug) => {
    const url = pageUrl(slug);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Link copied — add it in Menu Manager or Link Manager");
    } catch { toast.error("Could not copy link"); }
  };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const data = { ...form };
    if (!data.slug) data.slug = slugify(form.title);
    try {
      if (editing === "new") await api.post("/pages", { data });
      else await api.put(`/pages/${editing}`, { data });
      toast.success("Page saved"); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const togglePub = async (p) => {
    try { await api.put(`/pages/${p.id}`, { data: { ...p, published: !p.published } }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/pages/${confirm.id}`); toast.success("Page deleted"); setConfirm(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Custom Pages" subtitle="Create standalone pages (About, Legal, Careers…). They are themed automatically and live at /p/<slug>. Copy the page link and place it anywhere via the Menu Manager or Central Link Manager."
        action={canEdit && <button type="button" onClick={openNew} className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="page-add-btn"><Plus size={16} /> New Page</button>} />

      {items.length === 0 ? <Panel><EmptyState>No custom pages yet. Click "New Page" to create one.</EmptyState></Panel> : (
        <Panel>
          <div className="divide-y divide-[#203E70]">
            {items.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-4" data-testid={`page-row-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium truncate ${p.published ? "text-white" : "text-slate-500"}`}>{p.title}</p>
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${p.published ? "bg-[#D8AE4E]/15 text-[#D8AE4E]" : "bg-slate-700/40 text-slate-400"}`}>{p.published ? "Published" : "Draft"}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 truncate font-mono">/p/{p.slug}</p>
                </div>
                <button type="button" onClick={() => copyLink(p.slug)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" title="Copy page link" data-testid={`page-copy-${p.id}`}><Copy size={15} /></button>
                {p.published && <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-[#D8AE4E]" title="Open" data-testid={`page-open-${p.id}`}><ExternalLink size={15} /></a>}
                {canEdit && <button type="button" onClick={() => togglePub(p)} className={`p-2 ${p.published ? "text-[#D8AE4E]" : "text-slate-500"}`} title="Publish/Unpublish" data-testid={`page-pub-${p.id}`}>{p.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>}
                {canEdit && <button type="button" onClick={() => openEdit(p)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" data-testid={`page-edit-${p.id}`}><Pencil size={15} /></button>}
                {canEdit && <button type="button" onClick={() => setConfirm(p)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`page-delete-${p.id}`}><Trash2 size={15} /></button>}
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white max-h-[85vh] overflow-y-auto admin-scroll max-w-4xl">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "New Page" : "Edit Page"}</DialogTitle><DialogDescription className="text-slate-400 text-sm">Pages render in the site theme at /p/&lt;slug&gt;. After saving, copy the link to add it anywhere.</DialogDescription></DialogHeader>

          <div className="flex items-center gap-3">
            <div className="inline-flex rounded border border-[#203E70] overflow-hidden text-sm">
              <button type="button" onClick={() => setView("edit")} className={`px-4 py-1.5 transition-colors ${view === "edit" ? "bg-[#D8AE4E] text-[#06122E] font-medium" : "text-slate-300 hover:bg-[#102653]"}`} data-testid="page-view-edit">Edit</button>
              <button type="button" onClick={() => setView("preview")} className={`px-4 py-1.5 transition-colors ${view === "preview" ? "bg-[#D8AE4E] text-[#06122E] font-medium" : "text-slate-300 hover:bg-[#102653]"}`} data-testid="page-view-preview">Preview</button>
            </div>
            {view === "preview" && (
              <div className="inline-flex rounded border border-[#203E70] overflow-hidden ml-auto">
                <button type="button" onClick={() => setDevice("desktop")} title="Desktop" className={`p-1.5 ${device === "desktop" ? "bg-[#102653] text-[#D8AE4E]" : "text-slate-400 hover:text-white"}`} data-testid="page-device-desktop"><Monitor size={16} /></button>
                <button type="button" onClick={() => setDevice("mobile")} title="Mobile" className={`p-1.5 ${device === "mobile" ? "bg-[#102653] text-[#D8AE4E]" : "text-slate-400 hover:text-white"}`} data-testid="page-device-mobile"><Smartphone size={16} /></button>
              </div>
            )}
          </div>

          {view === "edit" ? (
          <div className="space-y-4">
            <TextInput label="Title *" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} testid="page-field-title" />
            <TextInput label="URL Slug (e.g. about-us)" value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} hint="Page will live at /p/<slug>" testid="page-field-slug" />
            <TextInput label="Subtitle (shown under the title in the hero)" value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} testid="page-field-subtitle" />
            <TextInput label="Hero Background Image URL (optional)" value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} testid="page-field-image" />
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Content</label>
              <RichTextEditor key={editing} value={form.body || ""} onChange={(html) => setForm((f) => ({ ...f, body: html }))} testid="page-field-body" />
            </div>
            <div className="flex flex-wrap gap-6 pt-2 border-t border-[#203E70]">
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published !== false} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="page-field-published" /> Published (uncheck to keep as a hidden draft)</label>
            </div>
            <div className="pt-2 border-t border-[#203E70] space-y-4">
              <p className="overline text-[#D8AE4E]">Search &amp; Social (SEO)</p>
              <TextInput label="SEO Title (browser tab & search results — defaults to page title)" value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} testid="page-field-seo-title" />
              <TextArea label="Meta Description (shown in search results)" rows={2} value={form.seo_description || ""} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} testid="page-field-seo-desc" />
              <TextInput label="Social Preview Image URL (Open Graph)" value={form.og_image || ""} onChange={(e) => setForm({ ...form, og_image: e.target.value })} hint="Shown when the page is shared on social media" testid="page-field-og-image" />
              {form.og_image && <img src={form.og_image} alt="Social preview" className="rounded border border-[#203E70] max-h-32" />}
            </div>
          </div>
          ) : (
          <div className="bg-[#030A1F] rounded-lg p-4 border border-[#203E70]" data-testid="page-preview">
            <div className="mx-auto rounded-lg border border-[#203E70] overflow-hidden transition-all duration-300 shadow-2xl" style={{ maxWidth: device === "mobile" ? 390 : "100%" }}>
              <div className="relative bg-[#06122E] px-6 py-10 border-b border-[#203E70] overflow-hidden">
                {form.image && <img src={form.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />}
                <div className="relative">
                  <p className="overline text-[#D8AE4E] mb-2">Cresent Bank</p>
                  <h1 className="font-serif font-bold text-3xl text-white leading-tight">{form.title || "Untitled page"}</h1>
                  {form.subtitle && <p className="text-slate-300 mt-3 leading-relaxed">{form.subtitle}</p>}
                </div>
              </div>
              <div className="bg-[#05070D] px-6 py-8">
                {form.body ? <div className="cb-prose text-slate-300" data-testid="page-preview-body" dangerouslySetInnerHTML={{ __html: form.body }} /> : <p className="text-slate-500 text-sm">No content yet — switch to Edit to add content.</p>}
              </div>
            </div>
          </div>
          )}

          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} className="btn-gold px-4 py-2 text-sm" data-testid="page-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <AlertDialogHeader><AlertDialogTitle>Delete this page?</AlertDialogTitle><AlertDialogDescription className="text-slate-400">"{confirm?.title}" will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#203E70] text-white hover:bg-[#102653]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="page-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
